import { HttpClient, HttpDownloadProgressEvent, HttpEvent, HttpEventType } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DwOrmDataService, DwOrmDataServiceToken, DwQuery } from '@devwareapps/devware-cap';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap } from 'rxjs/operators';

import {
  AcsAreaOfOperationEntity,
  AcsAreaOfOperationTaskEntity,
  AcsCodeEntity,
  AppMetaData,
  CourseModuleEntity,
  QuestionEntity,
  QuestionStatusAllItems,
  QuestionTypeAllItems,
  QuizQuestionLinkEntity,
  QuizTypeAllItems,
  ReferenceStatusAllItems
} from '../../../meta-data/app-meta-data.service';
import {
  AcsElement, BankQuestion, Difficulty, ExistingQuiz, GeneratedQuestion, GenerationResult, ProCourse, ProModule, QuestionExclusion, QuizNameItem,
  VariationStyle
} from '../models/quiz-builder-pro.model';
import { DraftAnswer, DraftQuestionType } from '../models/quiz-builder.model';
import { acsElements, shuffled, TYPE_LABELS } from '../components/quiz-builder-pro-page/quiz-builder-pro.util';
import { AI_PROXY, aiSource } from '../components/quiz-builder-page/quiz-builder.util';
import { stripHtml } from '../components/quiz-insights-grid-renderer/quiz-insights-view.util';

const SKILLS_CODE_TYPE = 3;
const TYPE_CODES: { [questionTypeId: number]: DraftQuestionType } = {
  [QuestionTypeAllItems.TrueOrFalse]: 'TF',
  [QuestionTypeAllItems.MultipleChoice]: 'MC',
  [QuestionTypeAllItems.MultipleAnswer]: 'MA'
};
const QUIZ_TYPE_LABELS: { [quizTypeId: number]: string } = {
  [QuizTypeAllItems.CourseFinal]: 'Course final',
  [QuizTypeAllItems.ModuleFinal]: 'Module final'
};

const usable = (q: QuestionEntity) =>
  q.QuestionStatusId === QuestionStatusAllItems.Active || q.QuestionStatusId === QuestionStatusAllItems.Draft;

function questionAnswersQuery(): DwQuery {
  const answers = AppMetaData.QuestionAnswer.CreateQueryBuilder();
  answers.addFields(f => [f.QuestionAnswerId, f.QuestionId, f.AnswerText, f.IsCorrectAnswer, f.AnswerOrder, f.ExplanationText]);
  answers.addOrderBy(a => a.AnswerOrder);
  return answers.query;
}

function toBankQuestion(q: QuestionEntity): BankQuestion {
  const type = TYPE_CODES[q.QuestionTypeId] || 'MC';
  return {
    id: q.QuestionId,
    name: q.QuestionName || '',
    text: stripHtml(q.Question),
    moduleId: q.CourseModuleId ?? null,
    type,
    typeLabel: TYPE_LABELS[type],
    answers: (q.QuestionAnswer || []).map(a => ({ text: stripHtml(a.AnswerText), correct: !!a.IsCorrectAnswer })),
    // The bank explains per answer; the correct answers' explanations stand in for the question's.
    explanation: (q.QuestionAnswer || []).filter(a => a.IsCorrectAnswer).map(a => stripHtml(a.ExplanationText || '').trim())
      .filter(Boolean).join(' ')
  };
}

/**
 * Read-only data for the Quiz Builder Pro prototype. Browsing data is cached; generation refreshes bank context.
 * ponytail: loads a course's whole bank at once (low thousands of rows on TEST); page it if banks grow.
 */
@Injectable({ providedIn: 'root' })
export class QuizBuilderProService {
  private courses$: Observable<{ courses: ProCourse[]; modules: ProModule[] }>;
  private readonly acs = new Map<number, Observable<AcsElement[]>>();
  private readonly bank = new Map<number, Observable<BankQuestion[]>>();
  private readonly quizzes = new Map<number, Observable<ExistingQuiz[]>>();

  constructor(
    @Inject(DwOrmDataServiceToken) private readonly dwOrmDataService: DwOrmDataService,
    private readonly http: HttpClient,
  ) { }

  courses(): Observable<{ courses: ProCourse[]; modules: ProModule[] }> {
    if (!this.courses$) {
      const courses = AppMetaData.Course.CreateQueryBuilder();
      courses.addFields(f => [f.CourseId, f.CourseName, f.AcsTestId, f.CourseStatusId, f.CourseOrder, f.DefaultSchoolCourse]);
      const modules = AppMetaData.CourseModule.CreateQueryBuilder();
      modules.addFields(f => [f.CourseModuleId, f.CourseId, f.ModuleName, f.ModuleOrder]);
      this.courses$ = forkJoin([
        this.dwOrmDataService.executeQuery(courses),
        this.dwOrmDataService.executeQuery(modules)
      ]).pipe(
        map(([c, m]) => {
          const active = c.filter(x => x.CourseStatusId === ReferenceStatusAllItems.Active)
            .sort((a, b) => (a.CourseOrder ?? 999) - (b.CourseOrder ?? 999) || a.CourseName.localeCompare(b.CourseName));
          const courseIds = new Set(active.map(x => x.CourseId));
          return {
            courses: active.map(x => ({ id: x.CourseId, name: x.CourseName, acsTestId: x.AcsTestId ?? null, isDefault: !!x.DefaultSchoolCourse })),
            modules: m.filter(x => courseIds.has(x.CourseId)).sort((a, b) => (a.ModuleOrder ?? 0) - (b.ModuleOrder ?? 0))
              // Names like "Module 3 - Learn Your Aircraft" already carry the order; don't prefix "3 — " again.
              .map(x => ({ id: x.CourseModuleId, courseId: x.CourseId, label: /^module\b/i.test(x.ModuleName || '') ? x.ModuleName
                : `${x.ModuleOrder ?? ''} — ${x.ModuleName}`.replace(/^ — /, '') }))
          };
        }),
        shareReplay(1)
      );
    }
    return this.courses$;
  }

  /** Knowledge and risk-management elements of an ACS test, with written-test miss rates. */
  acsElements(acsTestId: number): Observable<AcsElement[]> {
    if (!this.acs.has(acsTestId)) {
      const areas = AppMetaData.AcsAreaOfOperation.CreateQueryBuilder();
      areas.addFilterAnd(f => f.Equal(a => a.AcsTestId, acsTestId));
      areas.addOrderBy(a => a.AreaOfOperationOrder);
      const tasks = AppMetaData.AcsAreaOfOperationTask.CreateQueryBuilder();
      tasks.addOrderBy(a => a.TaskOrder);
      const codes = AppMetaData.AcsCode.CreateQueryBuilder();
      codes.addOrderBy(a => a.AcsCodeOrder);
      tasks.addPrefetch(r => r.AcsCode, codes.query);
      areas.addPrefetch(r => r.AcsAreaOfOperationTask, tasks.query);
      const results = AppMetaData.AcsWrittenResult.CreateQueryBuilder();
      results.addFields(f => [f.AcsWrittenResultId]);
      results.addFilterAnd(f => f.Equal(a => a.AcsTestId, acsTestId));
      const missed = AppMetaData.AcsWrittenResultMissedCode.CreateQueryBuilder();
      missed.addFields(f => [f.AcsWrittenResultId, f.AcsCodeId]);

      this.acs.set(acsTestId, forkJoin([
        this.dwOrmDataService.executeQuery<AcsAreaOfOperationEntity>(areas),
        this.dwOrmDataService.executeQuery(results),
        this.dwOrmDataService.executeQuery(missed)
      ]).pipe(
        map(([a, r, m]) => {
          const resultIds = new Set(r.map(x => x.AcsWrittenResultId));
          const label = (code: string, name: string) => [code, name].filter(Boolean).join('. ');
          const codeList: Parameters<typeof acsElements>[0] = [];
          a.forEach(area => (area.AcsAreaOfOperationTask || []).forEach((task: AcsAreaOfOperationTaskEntity) =>
            (task.AcsCode || []).forEach((c: AcsCodeEntity) => {
              if (c.AscCodeTypeId !== SKILLS_CODE_TYPE && c.AcsCode) {
                codeList.push({ id: c.AcsCodeId, code: c.AcsCode, description: c.AscCodeDescription || '',
                  area: label(area.AreaOfOperationCode, area.AreaOfOperationName), task: label(task.TaskCode, task.TaskName) });
              }
            })));
          // A result can list the same code twice; count it once.
          const missedIds = [...new Set(m.filter(x => x.AcsCodeId && resultIds.has(x.AcsWrittenResultId))
            .map(x => `${x.AcsWrittenResultId}:${x.AcsCodeId}`))].map(key => Number(key.split(':')[1]));
          return acsElements(codeList, resultIds.size, missedIds);
        }),
        shareReplay(1)
      ));
    }
    return this.acs.get(acsTestId);
  }

  /** Active and draft bank questions of a course, with answers. */
  bankQuestions(courseId: number, refresh = false): Observable<BankQuestion[]> {
    if (refresh) { this.bank.delete(courseId); }
    if (!this.bank.has(courseId)) {
      const questions = AppMetaData.Question.CreateQueryBuilder();
      questions.addFields(f => [f.QuestionId, f.QuestionName, f.Question, f.QuestionTypeId, f.CourseModuleId, f.QuestionStatusId]);
      questions.addFilterAnd(f => f.Equal(a => a.CourseId, courseId));
      questions.addPrefetch(r => r.QuestionAnswer, questionAnswersQuery());
      this.bank.set(courseId, this.dwOrmDataService.executeQuery<QuestionEntity>(questions).pipe(
        map(rows => rows.filter(usable).map(toBankQuestion)),
        shareReplay(1)
      ));
    }
    return this.bank.get(courseId);
  }

  /** Active CAP quizzes used by the course's lessons, in module and lesson order. Cached per course. */
  courseQuizzes(courseId: number): Observable<ExistingQuiz[]> {
    if (!this.quizzes.has(courseId)) {
      const modules = AppMetaData.CourseModule.CreateQueryBuilder();
      modules.addFields(f => [f.CourseModuleId, f.CourseId, f.ModuleOrder]);
      modules.addFilterAnd(f => f.Equal(a => a.CourseId, courseId));
      modules.addOrderBy(a => a.ModuleOrder);
      const lessons = AppMetaData.CourseLesson.CreateQueryBuilder();
      lessons.addFields(f => [f.CourseLessonId, f.CourseModuleId, f.QuizId, f.LessonOrder]);
      lessons.addOrderBy(a => a.LessonOrder);
      const quiz = AppMetaData.Quiz.CreateQueryBuilder();
      quiz.addFields(f => [f.QuizId, f.QuizName, f.QuizTypeId, f.QuizStatusId]);
      const links = AppMetaData.QuizQuestionLink.CreateQueryBuilder();
      links.addFields(f => [f.QuizQuestionLinkId, f.QuizId, f.QuestionId, f.IsDeleted]);
      quiz.addPrefetch(r => r.QuizQuestionLink, links.query);
      lessons.addPrefetch(r => r.Quiz, quiz.query);
      modules.addPrefetch(r => r.CourseLesson, lessons.query);
      this.quizzes.set(courseId, this.dwOrmDataService.executeQuery<CourseModuleEntity>(modules).pipe(
        map(rows => {
          const found = new Map<number, ExistingQuiz>();
          rows.forEach(m => (m.CourseLesson || []).forEach(lesson => {
            const q = lesson.Quiz;
            // Legacy quizzes can have no status; only explicitly inactive or deleted ones are hidden.
            if (!q || (q.QuizStatusId ?? ReferenceStatusAllItems.Active) !== ReferenceStatusAllItems.Active) { return; }
            const existing = found.get(q.QuizId);
            if (existing) {
              if (!existing.moduleIds.includes(m.CourseModuleId)) { existing.moduleIds.push(m.CourseModuleId); }
              return;
            }
            // Legacy links can have a null IsDeleted; only explicitly deleted links are excluded.
            const count = new Set((q.QuizQuestionLink || []).filter(l => l.IsDeleted !== true).map(l => l.QuestionId)).size;
            found.set(q.QuizId, { id: q.QuizId, name: q.QuizName || `Quiz #${q.QuizId}`, moduleIds: [m.CourseModuleId],
              questionCount: count,
              meta: [QUIZ_TYPE_LABELS[q.QuizTypeId], `${count} question${count === 1 ? '' : 's'}`].filter(Boolean).join(' · ') });
          }));
          return [...found.values()];
        }),
        // Don't cache a failure; the next course selection retries.
        catchError(err => { this.quizzes.delete(courseId); return throwError(err); }),
        shareReplay(1)
      ));
    }
    return this.quizzes.get(courseId);
  }

  /** A quiz's linked questions in quiz order; retired (inactive or deleted) questions are skipped and counted. */
  quizQuestions(quizId: number): Observable<{ questions: BankQuestion[]; skipped: number }> {
    const links = AppMetaData.QuizQuestionLink.CreateQueryBuilder();
    links.addFields(f => [f.QuizQuestionLinkId, f.QuizId, f.QuestionId, f.QuestionOrder, f.IsDeleted]);
    links.addFilterAnd(f => f.Equal(a => a.QuizId, quizId));
    links.addOrderBy(a => a.QuestionOrder);
    const question = AppMetaData.Question.CreateQueryBuilder();
    question.addFields(f => [f.QuestionId, f.QuestionName, f.Question, f.QuestionTypeId, f.CourseModuleId, f.QuestionStatusId]);
    question.addPrefetch(r => r.QuestionAnswer, questionAnswersQuery());
    links.addPrefetch(r => r.Question, question.query);
    return this.dwOrmDataService.executeQuery<QuizQuestionLinkEntity>(links).pipe(map(rows => {
      const seen = new Set<number>();
      const linked = rows.filter(l => l.IsDeleted !== true && l.Question && !seen.has(l.QuestionId) && seen.add(l.QuestionId))
        .map(l => l.Question);
      const questions = linked.filter(usable).map(toBankQuestion);
      return { questions, skipped: linked.length - questions.length };
    }));
  }

  suggestName(course: string, module: string, mode: string, count: number, items: QuizNameItem[]): Observable<string> {
    return this.http.post<{ name: string }>(`${AI_PROXY}/suggest-name`, { course, module, mode, count, items })
      .pipe(map(res => res.name));
  }

  /** Streams an AI explanation; each emission is the full text so far. */
  explain(type: DraftQuestionType, question: string, answers: DraftAnswer[], reference: string): Observable<string> {
    return this.http.post(`${AI_PROXY}/explain`, {
      type, question, reference, answers: answers.map(a => ({ text: a.text, correct: a.correct }))
    }, { observe: 'events', reportProgress: true, responseType: 'text' }).pipe(
      filter((e: HttpEvent<string>) => e.type === HttpEventType.DownloadProgress || e.type === HttpEventType.Response),
      map(e => e.type === HttpEventType.Response ? e.body : (e as HttpDownloadProgressEvent).partialText || ''),
      // Text responses leave the proxy's JSON error unparsed; parse it so proxyError() can read it.
      catchError(err => {
        let error: unknown;
        try { error = JSON.parse(err.error); } catch { error = undefined; }
        return throwError({ error });
      })
    );
  }

  /** Rewrites of one question in a style: same fact, same correct answer (the proxy's /variants). */
  variations(q: GeneratedQuestion, style: VariationStyle, count: number): Observable<{ question: string; answers: DraftAnswer[] }[]> {
    return this.http.post<{ versions: { question: string; answers: DraftAnswer[] }[] }>(`${AI_PROXY}/variants`, {
      type: q.type, question: q.question, answers: q.answers.map(a => ({ text: a.text, correct: a.correct })),
      coverage: null, hint: style, count
    }).pipe(map(res => res.versions.map(v => ({
      question: v.question,
      // Drop the proxy's per-answer "why"; True/False keeps its fixed order.
      answers: (q.type === 'TF' ? v.answers : shuffled(v.answers)).map(a => ({ text: a.text, correct: a.correct }))
    }))));
  }

  /** AI writes the questions, TypeSafe Jev scores each one (scripts/quiz-builder-ai-proxy.js). */
  generate(course: string, elements: AcsElement[], count: number, firstId: number,
           courseId: number, moduleId: number | null, exclusions: QuestionExclusion[], keywords = '',
           difficulty: Difficulty = 'written'): Observable<GenerationResult> {
    type Raw = Omit<GeneratedQuestion, 'id' | 'ai'>;
    // Always load bank context, even when the author never opened the bank tab.
    return this.bankQuestions(courseId, true).pipe(
      catchError(() => throwError({ error: { error: 'Could not load the question bank for duplicate checks. Try again.' } })),
      switchMap(bank =>
      this.http.post<{ questions: Raw[]; model: string; skipped: number; similarityChecked: boolean }>(`${AI_PROXY}/generate`, {
        course,
        moduleId,
        firstId,
        count,
        keywords,
        difficulty,
        exclusions: [...bank.filter(q => q.text.trim()).map(q => ({ id: q.id, source: 'bank', text: q.text,
          type: q.type, moduleId: q.moduleId, answers: q.answers })), ...exclusions],
        codes: elements.map(e => ({ code: e.code, description: e.description, missRate: e.missRate }))
      })
    ), map(res => ({
      skipped: res.skipped,
      similarityChecked: res.similarityChecked,
      questions: res.questions.map((q, i) => ({
        ...q,
        // True/False keeps its fixed True, False order.
        answers: q.type === 'TF' ? q.answers : shuffled(q.answers),
        id: firstId - i,
        ai: aiSource(res.model)
      }))
    })));
  }
}
